import {
  useState, useEffect,
  useContext,
  createContext,
  useReducer
} from 'react';
import { User, Users, MapPin, Check, X, Calendar, Navigation, UserCheck, Loader2, Building, Bed, DollarSign } from 'lucide-react';
import RoomInfoModal from './RoomInfoModal.jsx';
import HotelPlanModal from './HotelPlanModal.jsx';
const baseApiURlTest = 'http://localhost:3000/viajes';
let baseURL  =  'https://bochoback.infinitecmexico.com/viajes';
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  baseURL = baseApiURlTest;
}

const BookingContext = createContext();

// ----- Seat layout helpers (sequential numbering) -----
const SEATS_PER_ROW = 4; // keep your 2–aisle–2 layout

// "3-2" -> 10 (sequential); "12-4" -> 48; numbers pass through
const parseSeatId = (id, seatsPerRow = SEATS_PER_ROW) => {
  if (typeof id === 'string' && id.includes('-')) {
    const [r, s] = id.split('-').map(Number);
    return (r - 1) * seatsPerRow + s;
  }
  return Number(id);
};

// Build seats as 1..N; keep row/position purely for layout/grouping
const createBusLayout = (totalSeats = 48, seatsPerRow = SEATS_PER_ROW) => {
  const seats = [];
  for (let i = 1; i <= totalSeats; i++) {
    const row = Math.ceil(i / seatsPerRow);
    const position = ((i - 1) % seatsPerRow) < (seatsPerRow / 2) ? 'left' : 'right';
    seats.push({
      id: i,
      number: i,
      row,
      position,
    });
  }
  return seats;
};

// Normalize any seats array coming from API to sequential-number shape
const normalizeBusSeats = (seatsFromApi, totalSeats = 48, seatsPerRow = SEATS_PER_ROW) => {
  if (!Array.isArray(seatsFromApi) || seatsFromApi.length === 0) {
    return createBusLayout(totalSeats, seatsPerRow);
  }

  return seatsFromApi.map((s) => {
    const raw = typeof s === 'object' ? (s.id ?? s.number ?? s) : s;
    const n = parseSeatId(raw, seatsPerRow);

    const row = Math.ceil(n / seatsPerRow);
    const position = ((n - 1) % seatsPerRow) < (seatsPerRow / 2) ? 'left' : 'right';

    return {
      id: n,
      number: n,
      row,
      position,
    };
  });
};

const normalizeSelectedRooms = (selectedRoomsRaw) => {
  if (!selectedRoomsRaw || typeof selectedRoomsRaw !== 'object') return {};

  const normalized = {};

  Object.entries(selectedRoomsRaw).forEach(([hotelId, roomTypes]) => {
    if (!roomTypes || typeof roomTypes !== 'object') return;

    normalized[hotelId] = {};
    Object.entries(roomTypes).forEach(([roomType, roomsValue]) => {
      if (Array.isArray(roomsValue)) {
        normalized[hotelId][roomType] = roomsValue;
        return;
      }

      const count = Number(roomsValue);
      if (Number.isFinite(count) && count > 0) {
        normalized[hotelId][roomType] = Array.from({ length: count }, () => ({
          adults: 0,
          juniors: 0,
          children: 0
        }));
      }
    });
  });

  return normalized;
};

const normalizeRoomAssignments = (roomAssignmentsRaw) => {
  if (!roomAssignmentsRaw) return {};

  // Legacy flat array shape from API:
  // [{ hotel, roomType, passengers, roomNumber, observations }]
  if (Array.isArray(roomAssignmentsRaw)) {
    return roomAssignmentsRaw.reduce((acc, item) => {
      const hotelId = item?.hotel;
      const roomType = item?.roomType;
      if (!hotelId || !roomType) return acc;

      if (!acc[hotelId]) acc[hotelId] = {};
      if (!acc[hotelId][roomType]) acc[hotelId][roomType] = [];

      acc[hotelId][roomType].push({
        roomNumber: Number(item?.roomNumber) || (acc[hotelId][roomType].length + 1),
        passengers: Array.isArray(item?.passengers) ? item.passengers : [],
        observations: item?.observations || ''
      });

      return acc;
    }, {});
  }

  if (typeof roomAssignmentsRaw !== 'object') return {};

  // Expected nested object shape
  const normalized = {};
  Object.entries(roomAssignmentsRaw).forEach(([hotelId, roomTypes]) => {
    if (!roomTypes || typeof roomTypes !== 'object') return;

    normalized[hotelId] = {};
    Object.entries(roomTypes).forEach(([roomType, rooms]) => {
      if (!Array.isArray(rooms)) {
        normalized[hotelId][roomType] = [];
        return;
      }

      normalized[hotelId][roomType] = rooms.map((room, idx) => ({
        roomNumber: Number(room?.roomNumber) || (idx + 1),
        passengers: Array.isArray(room?.passengers) ? room.passengers : [],
        observations: room?.observations || ''
      }));
    });
  });

  return normalized;
};

// Helper function to calculate total room capacity
const calculateTotalRoomCapacity = (selectedRooms) => {
  return Object.values(selectedRooms).reduce((total, hotel) => {
    return total + Object.entries(hotel).reduce((hotelTotal, [roomType, rooms]) => {
      const count = Array.isArray(rooms) ? rooms.length : 0;
      return hotelTotal + (ROOM_CAPACITIES[roomType] * count);
    }, 0);
  }, 0);
};

// Room capacity definitions
const ROOM_CAPACITIES = {
  roomsimple: 1,
  roomdoble: 2,
  roomtriple: 3,
  roomcuadruple: 4
};

// Mock booked seats (kept empty; will be normalized from API if present)
const bookedSeats = [];

// Helper function to calculate capacity for a specific hotel
const calculateHotelCapacity = (hotelRooms) => {
  return Object.entries(hotelRooms).reduce((total, [roomType, rooms]) => {
    const count = Array.isArray(rooms) ? rooms.length : 0;
    return total + (ROOM_CAPACITIES[roomType] * count);
  }, 0);
};

// Helper function to calculate anticipo per person
const calculateAnticipo = (tripInfo) => {
  const productCategory = tripInfo?.Product_Category?.toLowerCase();
  
  if (productCategory === 'avion') {
    return 4500;
  }
  
  if (productCategory === 'playa') {
    return 1500;
  }
  
  // Calculate based on departure and return dates
  if (tripInfo?.departureDate && tripInfo?.departureReturn) {
    try {
      const departure = new Date(tripInfo.departureDate);
      const returnDate = new Date(tripInfo.departureReturn);
      const diffTime = Math.abs(returnDate - departure);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Less than 3 nights = 1000, otherwise 2000
      return diffDays < 3 ? 1000 : 2000;
    } catch (error) {
      console.error('Error calculating nights:', error);
      return 1000; // Default fallback
    }
  }
  
  return 1000; // Default fallback
};

// Mock trip data
const tripData = {
  origin: 'Ciudad de México',
  destination: 'Guadalajara',
  departureDate: '2025-05-30',
  departureTime: '14:30',
  totalSeats: 48,
  availableSeats: 41
};

// Mock hotel data
const mockHotelData = [
  {
    hotel1: {
      name: 'Hotel Plaza Central',
      roomsimple: 3,
      roomdoble: 4,
      roomtriple: 2
    }
  },
  {
    hotel2: {
      name: 'Hotel Ejecutivo',
      roomsimple: 2,
      roomdoble: 3,
      roomtriple: 1
    }
  }
];

// Loading Spinner Component
const LoadingSpinner = () => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-800">Cargando información</h3>
          <p className="text-sm text-gray-600 mt-1">Conectando con el servidor...</p>
        </div>
      </div>
    </div>
  );
};

// Step Selector Component
const StepSelector = () => {
  const { state, dispatch } = useBookingContext();

  const canGoToHoteles = state.selectedSeats.length === state.passengerCount;
  const canGoToPasajeros = canGoToHoteles && Object.keys(state.selectedRooms).length > 0;

  const steps = [
    {
      id: 'asientos',
      label: 'Asientos',
      icon: Navigation,
      enabled: true,
      completed: state.selectedSeats.length === state.passengerCount
    },
    {
      id: 'hoteles',
      label: 'Hoteles',
      icon: Building,
      enabled: canGoToHoteles,
      completed: canGoToHoteles && Object.keys(state.selectedRooms).length > 0
    },
    {
      id: 'pasajeros',
      label: 'Pasajeros',
      icon: Users,
      enabled: canGoToPasajeros,
      completed: false
    }
  ];

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
      <h3 className="text-lg font-semibold mb-3 text-center">Proceso de Reserva</h3>
      <div className="flex space-x-4 justify-center">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = state.currentStep === step.id;
          const isCompleted = step.completed;

          return (
            <button
              key={step.id}
              onClick={() => step.enabled && dispatch({ type: 'SET_CURRENT_STEP', step: step.id })}
              disabled={!step.enabled}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors relative ${
                isCompleted
                  ? 'bg-green-500 text-white'
                  : isActive
                  ? 'bg-blue-500 text-white'
                  : step.enabled
                  ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{step.label}</span>
              {isCompleted && (
                <Check className="w-4 h-4 text-white absolute -top-1 -right-1 bg-green-600 rounded-full" />
              )}
              {index < steps.length - 1 && (
                <div className="absolute -right-6 top-1/2 transform -translate-y-1/2 w-4 h-0.5 bg-gray-300"></div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Final Confirmation Modal
const FinalConfirmationModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-md text-center">
        <div className="mb-4">
          <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">¡Reserva Confirmada!</h2>
          <p className="text-gray-600">Su reserva ha sido procesada exitosamente.</p>
        </div>

        <button
          onClick={onClose}
          className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-8 rounded-lg transition-colors text-lg font-medium"
        >
          OK
        </button>
      </div>
    </div>
  );
};

// API Status Component
const ApiStatus = () => {
  const { state } = useBookingContext();

  if (!state.apiData && !state.apiError) {
    return null;
  }

  return (
    <div className="mb-4">
      {state.apiData && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <h3 className="text-sm font-medium text-green-800 mb-1">
            ✅ Conexión exitosa con el servidor
          </h3>
          <p className="text-xs text-green-700">
            {state.apiData.existingBooking ? 'Reserva existente cargada correctamente' : 'Los datos se han cargado correctamente'}
          </p>
        </div>
      )}

      {state.apiError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <h3 className="text-sm font-medium text-red-800 mb-1">
            ❌ Error de conexión
          </h3>
          <p className="text-xs text-red-700">
            {state.apiError}
          </p>
        </div>
      )}
    </div>
  );
};

// Trip Information Header
const TripHeader = () => {
  const { state } = useBookingContext();
  const { tripInfo } = state;

  return (
    <div className="bg-white p-3 rounded-lg shadow-sm mb-3 overflow-x-auto">
      <div className="flex gap-6 whitespace-nowrap justify-between">

        <div className="flex items-center space-x-2">
          <Navigation className="w-4 h-4 text-blue-500" />
          <div>
            <p className="text-xs text-gray-600">Ruta</p>
            <p className="font-medium text-sm">
              {tripInfo.origin} → {tripInfo.destination}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-green-500" />
          <div>
            <p className="text-xs text-gray-600">Salida</p>
            <p className="font-medium text-sm">
              {tripInfo.departureDate} {tripInfo.departureTime}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Users className="w-4 h-4 text-purple-500" />
          <div>
            <p className="text-xs text-gray-600">Pasajeros</p>
            <p className="font-medium text-sm">{state.passengerCount}</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-orange-500" />
          <div>
            <p className="text-xs text-gray-600">Disponibles</p>
            <p className="font-medium text-sm text-green-600">
              {Math.max(0, (tripInfo.availableSeats || 0) - state.selectedSeats.length)}/{tripInfo.totalSeats || 0}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <DollarSign className="w-4 h-4 text-purple-500" />
          <div>
            <p className="text-xs text-gray-600">Anticipo</p>
            <p className="font-medium text-sm">{ (calculateAnticipo(tripInfo) * state.passengerCount).toFixed(2) }</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <DollarSign className="w-4 h-4 text-purple-500" />
          <div>
            <p className="text-xs text-gray-600">Total</p>
            <p className="font-medium text-sm">{state.total || 0}</p>
          </div>
        </div>

      </div>
    </div>

  );
};

// Hotel Room Selector Component
const HotelRoomSelector = () => {
  const { state, dispatch } = useBookingContext();
  const [modalOpen, setModalOpen] = useState(false);
  const [roomInfo, setRoomInfo] = useState({
    roomType: '',
    totalPeople: 0,
    hotelId: '',
    name: ''
  });

  if (state.currentStep !== 'hoteles') return null;

  const getRoomTypeName = (type) => {
    const types = {
      roomsimple: 'Habitación Simple (1 persona)',
      roomdoble: 'Habitación Doble (2 personas)',
      roomtriple: 'Habitación Triple (3 personas)',
      roomcuadruple: 'Habitación Cuádruple (4 personas)'
    };
    return types[type] || type;
  };

  const getTotalSelectedRooms = () => {
    return Object.values(state.selectedRooms).reduce((total, hotel) => {
      return total + Object.values(hotel).reduce((sum, roomsArray) => {
        const count = Array.isArray(roomsArray) ? roomsArray.length : 0;
        return sum + count;
      }, 0);
    }, 0);
  };

  const getHotelCapacity = (hotelId) => {
    const hotelRooms = state.selectedRooms[hotelId] || {};
    return calculateHotelCapacity(hotelRooms);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-center mb-4">Selección de Habitaciones</h2>

      {/* Capacity Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-blue-800">
          <strong>Pasajeros:</strong> {state.passengerCount} | 
          <strong> Total de hoteles seleccionados:</strong> {Object.keys(state.selectedRooms).filter(hotelId => Object.keys(state.selectedRooms[hotelId] || {}).length > 0).length}
        </p>
        <p className="text-xs text-blue-600 mt-1">
          Cada hotel debe acomodar a todos los pasajeros (es un tour). La capacidad por hotel no puede exceder {state.passengerCount} personas.
        </p>
      </div>

      {state.hotelData.map((hotelObj, index) => {
        const hotelId = Object.keys(hotelObj)[0];
        const hotelInfo = hotelObj[hotelId];
        const hotelName = hotelInfo.name || `Hotel ${index + 1}`;

        return (
          <div key={hotelId} className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium mb-2 flex items-center justify-between">
              <div className="flex items-center">
                <Building className="w-5 h-5 mr-2 text-blue-500" />
                {hotelName}
              </div>
              <div className="text-sm text-gray-600">
                Capacidad: {getHotelCapacity(hotelId)}/{state.passengerCount}
              </div>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Object.entries(hotelInfo).map(([roomType, available]) => {
                if (roomType === 'name' || roomType === 'personaadicional') return null;

                const selectedRooms = state.selectedRooms[hotelId]?.[roomType] || [];
                const selected = Array.isArray(selectedRooms) ? selectedRooms.length : 0;
                const roomCapacity = ROOM_CAPACITIES[roomType];
                const currentHotelCapacity = getHotelCapacity(hotelId);
                const wouldExceedCapacity = (currentHotelCapacity + roomCapacity) > state.passengerCount;

                return (
                  <div key={roomType} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Bed className="w-4 h-4 text-green-500" />
                        <span className="font-medium text-sm">{getRoomTypeName(roomType)}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {available} disponibles
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => dispatch({ type: 'DESELECT_ROOM', hotelId, roomType })}
                        disabled={selected === 0}
                        className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white flex items-center justify-center"
                      >
                        -
                      </button>

                      <span className="font-medium text-lg min-w-[2rem] text-center">
                        {selected}
                      </span>

                      <button
                        onClick={() => {
                          setRoomInfo({
                            hotelId,
                            roomType,
                            totalPeople: ROOM_CAPACITIES[roomType],
                            name: hotelName
                          });
                          setModalOpen(true);
                        }}
                        disabled={selected >= available || wouldExceedCapacity}
                        className="w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white flex items-center justify-center"
                        title={wouldExceedCapacity ? `Excedería la capacidad del hotel (${state.passengerCount} personas)` : ''}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Selected Rooms Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Resumen de Selección</h4>
        <p className="text-sm text-gray-600 mb-2">
          Total de habitaciones seleccionadas: {getTotalSelectedRooms()}
        </p>

        {Object.entries(state.selectedRooms).map(([hotelId, rooms]) => {
          if (Object.keys(rooms).length === 0) return null;

          const hotelName = state.hotelData.find(h => Object.keys(h)[0] === hotelId)?.[hotelId]?.name || hotelId;
          const hotelInfo = state.hotelData.find(h => Object.keys(h)[0] === hotelId)?.[hotelId];
          const hotelCapacity = getHotelCapacity(hotelId);

          return (
            <div key={hotelId} className="mb-2 p-2 bg-white rounded border">
              <p className="font-medium text-sm flex justify-between">
                {hotelName}
                <span className={`text-xs px-2 py-1 rounded ${hotelCapacity === state.passengerCount ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {hotelCapacity}/{state.passengerCount} personas
                </span>
              </p>
              <div className="ml-4 space-y-1">
                {Object.entries(rooms).map(([roomType, roomsArray]) => {
                  const count = Array.isArray(roomsArray) ? roomsArray.length : 0;
                  return (
                    <p key={roomType} className="text-sm">
                      {roomType === 'roomsimple' ? 'Habitación Simple' : roomType === 'roomdoble' ? 'Habitación Doble' : roomType === 'roomtriple' ? 'Habitación Triple' : 'Habitación Cuádruple'}: {count} habitación{count > 1 ? 'es' : ''}
                    </p>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal - placed at component level, outside of loops */}
      <RoomInfoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        hotelName={roomInfo.name}
        hotelPrecios={state.hotelPrecios}
        hotelId={roomInfo.hotelId}
        roomType={roomInfo.roomType}
        maxCapacity={roomInfo.totalPeople}
        onAddRoom={({ roomType, adults, juniors, children,price }) => {
          dispatch({
            type: 'SELECT_ROOM',
            hotelId: roomInfo.hotelId,
            roomType,
            adults,
            juniors,
            children
          });
          dispatch({
            type: 'ADD_ROOM_TOTAL',
            amount: price
          });
          setModalOpen(false);
        }}
      />
    </div>
  );
};

// Passenger Details Form (now includes seat assignment column)
const PassengerDetailsForm = () => {
  const { state, dispatch } = useBookingContext();
  const [localDetails, setLocalDetails] = useState(state.passengerDetails);
  const [showFinalModal, setShowFinalModal] = useState(false);
  const [showHotelPlanModal, setShowHotelPlanModal] = useState(false);

  useEffect(() => {
    setLocalDetails(state.passengerDetails);
  }, [state.passengerDetails]);

  // Initialize/repair passengerSeats mapping when entering this step or when seat pool changes
  useEffect(() => {
    if (state.currentStep !== 'pasajeros') return;

    const pool = state.selectedSeats;
    const count = state.passengerCount;
    if (!Array.isArray(pool) || pool.length < count) return;

    const current = (state.passengerSeats || []).filter(s => pool.includes(s));
    const assignedSet = new Set(current);
    const result = [...current];

    for (const s of pool) {
      if (result.length >= count) break;
      if (!assignedSet.has(s)) {
        result.push(s);
        assignedSet.add(s);
      }
    }

    const needsUpdate =
      result.length !== count ||
      result.some(s => !pool.includes(s));

    if (needsUpdate) {
      dispatch({ type: 'SET_PASSENGER_SEATS', seats: result.slice(0, count) });
    }
  }, [state.currentStep, state.selectedSeats, state.passengerCount, dispatch, state.passengerSeats]);

  if (state.currentStep !== 'pasajeros') return null;

  const assignedSeats = state.passengerSeats || [];
  const poolSeats = state.selectedSeats || [];

  const handleInputChange = (index, field, value) => {
    const newDetails = [...localDetails];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setLocalDetails(newDetails);
    dispatch({ type: 'SET_PASSENGER_DETAILS', details: newDetails });
  };

  const handleSeatChange = (index, value) => {
    const seatId = Number(value);
    if (!Number.isFinite(seatId)) return;
    dispatch({ type: 'SET_PASSENGER_SEAT', index, seatId });
  };

  const handleSubmitBooking = async () => {
    try {
      const passengersWithSeats = localDetails.map((p, idx) => ({
        ...p,
        seat: state.selectedSeats[idx] ?? null,
      }));

      // Transform roomAssignments from nested object to flat array
      const roomAssignmentsArray = [];
      Object.entries(state.roomAssignments || {}).forEach(([hotelId, roomTypes]) => {
        Object.entries(roomTypes).forEach(([roomType, rooms]) => {
          if (Array.isArray(rooms)) {
            rooms.forEach(room => {
              roomAssignmentsArray.push({
                hotel: hotelId,
                roomType: roomType,
                passengers: room.passengers || [],
                roomNumber: room.roomNumber || null,
                observations: room.observations || ''
              });
            });
          }
        });
      });

      const bookingData = {
        tripInfo: state.tripInfo,
        selectedSeats: state.selectedSeats,
        amount: state.total,
        selectedRooms: state.selectedRooms,
        roomAssignments: roomAssignmentsArray,
        passengerDetails: passengersWithSeats,
        id_deal: state.urlParameters?.id_deal,
        id_viaje: state.urlParameters?.id_viaje || state.urlParameters?.id_trip,
        id_trip: state.urlParameters?.id_trip,
        advance: (calculateAnticipo(state.tripInfo) * state.passengerCount),
      };

      const response = await fetch(baseURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });


      if (response.ok) {
        const result = await response.json();
        console.log('Booking confirmed:', result);
        setShowFinalModal(true);
      } else {
        throw new Error('Error al procesar la reserva');
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Error al confirmar la reserva. Por favor, inténtelo de nuevo.');
    }
  };

  const seatsAssignedOk =
    Array.isArray(assignedSeats) &&
    assignedSeats.length === state.passengerCount &&
    new Set(assignedSeats).size === assignedSeats.length &&
    assignedSeats.every(s => poolSeats.includes(s));

  const isValid =
    localDetails.every((passenger, index) => {
      const hasRequiredFields = passenger.name.trim() !== '' && passenger.birthday.trim() !== '';
      if (index === 0) {
        return hasRequiredFields && passenger.phone.trim() !== '';
      }
      return hasRequiredFields;
    }) && seatsAssignedOk;

  return (
    <>
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <UserCheck className="w-5 h-5 mr-2" />
          Datos de Pasajeros
        </h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Asientos seleccionados:</strong> {poolSeats.join(', ')} | 
            <strong> Habitaciones:</strong> {Object.values(state.selectedRooms).reduce((total, hotel) => {
              return total + Object.values(hotel).reduce((sum, roomsArray) => {
                const count = Array.isArray(roomsArray) ? roomsArray.length : 0;
                return sum + count;
              }, 0);
            }, 0)} seleccionadas
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Nota:</strong> El número de teléfono es obligatorio solo para el pasajero principal. 
            Para pasajeros adicionales, el teléfono es opcional. También puedes asignar manualmente el asiento a cada pasajero.
          </p>
        </div>

        <div className="space-y-4">
          {localDetails.map((passenger, index) => {
            const isMainPassenger = index === 0;
            const currentSeat = assignedSeats[index];

            return (
              <div key={index} className={`border rounded-lg p-4 ${isMainPassenger ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                <h3 className="font-medium mb-3 flex items-center justify-between">
                  <span className="flex items-center">
                    {isMainPassenger && <User className="w-4 h-4 mr-2 text-blue-600" />}
                    {isMainPassenger ? 'Pasajero Principal' : `Pasajero ${index + 1}`}
                    {isMainPassenger && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Principal</span>}
                  </span>
                  <span className="text-xs text-gray-700">
                    Asiento asignado: <strong>{currentSeat ?? '—'}</strong>
                  </span>
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      value={passenger.name}
                      onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ingrese nombre completo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      value={passenger.birthday}
                      onChange={(e) => handleInputChange(index, 'birthday', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoría *
                    </label>
                    <select
                      value={passenger.ageCategory || 'adulto'}
                      onChange={(e) => handleInputChange(index, 'ageCategory', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="adulto">Adulto</option>
                      <option value="junior">Junior</option>
                      <option value="niño">Niño</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono {isMainPassenger ? '*' : '(Opc)'}
                    </label>
                    <input
                      type="tel"
                      value={passenger.phone}
                      onChange={(e) => handleInputChange(index, 'phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isMainPassenger ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                      }`}
                      placeholder={isMainPassenger ? "Teléfono" : "Opcional"}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asiento *
                    </label>
                    <select
                      value={currentSeat ?? ''}
                      onChange={(e) => handleSeatChange(index, e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="" disabled>Asiento</option>
                      {poolSeats.map((s) => {
                        const occupiedByOther = assignedSeats.findIndex((x, i) => i !== index && x === s) !== -1;
                        return (
                          <option key={s} value={s}>
                            {s}{occupiedByOther ? ' (ocupado)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 space-y-3">
          <button
            onClick={handleSubmitBooking}
            disabled={!isValid}
            className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg transition-colors text-lg font-medium"
          >
            Confirmar Reserva Completa
          </button>
          
          <button
            onClick={() => setShowHotelPlanModal(true)}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-lg transition-colors text-lg font-medium flex items-center justify-center space-x-2"
          >
            <Building className="w-5 h-5" />
            <span>Plan de Hoteles</span>
          </button>
          
          {!seatsAssignedOk && (
            <p className="text-xs text-red-600 mt-2">
              Asigna un asiento único y válido a cada pasajero.
            </p>
          )}
        </div>
      </div>

      <FinalConfirmationModal 
        isOpen={showFinalModal}
        onClose={() => setShowFinalModal(false)}
      />

      <HotelPlanModal
        isOpen={showHotelPlanModal}
        onClose={() => setShowHotelPlanModal(false)}
        selectedRooms={state.selectedRooms}
        hotelData={state.hotelData}
        passengerDetails={localDetails}
        existingRoomAssignments={state.roomAssignments}
        onSave={(roomAssignments) => {
          dispatch({ type: 'SET_ROOM_ASSIGNMENTS', roomAssignments });
          console.log('Room assignments saved:', roomAssignments);
        }}
      />
    </>
  );
};

// Individual Seat Component
const Seat = ({ seat }) => {

 
  const { state, dispatch } = useBookingContext();
  const { selectedSeats, bookedSeats } = state;

   if (!seat) return null;

  const isBooked = bookedSeats.includes(seat.id);
  const isSelected = selectedSeats.includes(seat.id);
  const canSelect = !isBooked && selectedSeats.length < state.passengerCount;

  const getSeatStyle = () => {
    if (isBooked) return 'bg-red-500 cursor-not-allowed';
    if (isSelected) return 'bg-blue-500 hover:bg-blue-600';
    if (canSelect || selectedSeats.includes(seat.id)) return 'bg-green-500 hover:bg-green-600 cursor-pointer';
    return 'bg-gray-300 cursor-not-allowed';
  };

  const handleSeatClick = () => {
    if (isBooked) return;

    if (isSelected) {
      dispatch({ type: 'DESELECT_SEAT', seatId: seat.id });
    } else if (canSelect) {
      dispatch({ type: 'SELECT_SEAT', seatId: seat.id });
    }
  };

  return (
    <div
      className={`w-12 h-12 rounded-lg flex items-center justify-center text-white text-sm font-medium transition-colors ${getSeatStyle()}`}
      onClick={handleSeatClick}
      title={`Asiento ${seat.number} - ${isBooked ? 'Ocupado' : isSelected ? 'Seleccionado' : 'Disponible'}`}
    >
      {seat.number}
    </div>
  );
};

// Bus Layout Component
const BusLayout = () => {
  const { state } = useBookingContext();
  const { busSeats, tripInfo } = state;

  if (state.currentStep !== 'asientos') return null;

  const lowerDeckSeats = busSeats.filter(seat => seat.row <= 12);
  const upperDeckSeats = busSeats.filter(seat => seat.row > 12);
  const hasUpperDeck = upperDeckSeats.length > 0;

  const groupByRow = (seats) => {
    return seats.reduce((acc, seat) => {
      if (!acc[seat.row]) acc[seat.row] = [];
      acc[seat.row].push(seat);
      return acc;
    }, {});
  };

  const lowerDeckByRow = groupByRow(lowerDeckSeats);
  const upperDeckByRow = groupByRow(upperDeckSeats);

  return (
    <div className="bg-gray-100 p-6 rounded-lg space-y-10">
      <div className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium text-center mb-5">
        {tripInfo?.typeBus}
      </div>

      <div className={`w-full ${hasUpperDeck ? 'flex gap-6' : 'block'}`}>
        <div id="piso-1" className="flex-1">
          <div className="mb-4 flex items-center justify-center">
            <div className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium text-center">
              Conductor
            </div>
          </div>

          <div className="space-y-3">
            {Object.entries(lowerDeckByRow).map(([row, seats]) => (
              <div key={row} className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <Seat seat={seats[0]} />
                  <Seat seat={seats[1]} />
                </div>
                <div className="text-sm text-gray-500 font-medium w-8 text-center">
                  {row}
                </div>
                <div className="flex space-x-2">
                  <Seat seat={seats[2]} />
                  <Seat seat={seats[3]} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {hasUpperDeck && (
          <div id="piso-2" className="flex-1">
            <div className="mb-4 flex items-center justify-center">
              <div className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium text-center">
                Planta Alta Frente
              </div>
            </div>

            <div className="space-y-3">
              {Object.entries(upperDeckByRow).map(([row, seats]) => (
                <div key={row} className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <Seat seat={seats[0]} />
                    <Seat seat={seats[1]} />
                  </div>
                  <div className="text-sm text-gray-500 font-medium w-8 text-center">
                    {row}
                  </div>
                  <div className="flex space-x-2">
                    <Seat seat={seats[2]} />
                    <Seat seat={seats[3]} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Top Controls Component
const TopControls = () => {
  const { state, dispatch } = useBookingContext();

  const getSelectionCount = () => {
    if (state.currentStep === 'asientos') {
      return state.selectedSeats.length;
    } else {
      return Object.values(state.selectedRooms).reduce((total, hotel) => {
        return total + Object.values(hotel).reduce((sum, roomsArray) => {
          const count = Array.isArray(roomsArray) ? roomsArray.length : 0;
          return sum + count;
        }, 0);
      }, 0);
    }
  };

  const getSelectedSummary = () => {
    if (state.currentStep === 'asientos') {
      return state.selectedSeats.length > 0
        ? state.selectedSeats.map(seatId => (
            <span key={seatId} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
              {seatId}
            </span>
          ))
        : <p className="text-gray-500 text-xs">Ningún asiento seleccionado</p>;
    } else if (state.currentStep === 'hoteles') {
      const selections = [];
      Object.entries(state.selectedRooms).forEach(([hotelId, rooms]) => {
        Object.entries(rooms).forEach(([roomType, roomsArray]) => {
          const count = Array.isArray(roomsArray) ? roomsArray.length : 0;
          if (count > 0) {
            selections.push(
              <span key={`${hotelId}-${roomType}`} className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
                {roomType}: {count}
              </span>
            );
          }
        });
      });

      return selections.length > 0
        ? selections
        : <p className="text-gray-500 text-xs">Ninguna habitación seleccionada</p>;
    }
    return null;
  };

  if (state.currentStep === 'pasajeros') return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
      <div className="bg-white p-3 rounded-lg shadow-sm">
        <h3 className="text-sm font-medium mb-2 flex items-center">
          <Users className="w-4 h-4 mr-1" />
          Pasajeros
        </h3>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Selecciona número de pasajeros
          </label>

          <select
            disabled={state.currentStep !== 'asientos'}
            value={state.passengerCount}
            onChange={(e) =>
              dispatch({ type: 'SET_PASSENGER_COUNT', count: parseInt(e.target.value) })
            }
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
          >
            {[...Array(Math.max(1, state?.tripInfo?.availableSeats || 1))].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white p-3 rounded-lg shadow-sm">
        <h3 className="text-sm font-medium mb-2 flex items-center">
          <MapPin className="w-4 h-4 mr-1" />
          Seleccionados ({getSelectionCount()}{state.currentStep === 'asientos' ? `/${state.passengerCount}` : ''})
        </h3>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {getSelectedSummary()}
          </div>

          <div className="flex space-x-1">
            <button
              onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}
              disabled={getSelectionCount() === 0}
              className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-1.5 px-2 rounded text-xs transition-colors flex items-center justify-center"
            >
              <X className="w-3 h-3 mr-1" />
              Limpiar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Legend Component
const SeatLegend = () => {
  const { state } = useBookingContext();

  if (state.currentStep !== 'asientos') return null;

  return (
    <div className="bg-white p-3 rounded-lg shadow-sm">
      <h3 className="text-sm font-medium mb-2">Leyenda</h3>
      <div className="space-y-1.5">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span className="text-xs">Disponible</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-blue-500 rounded"></div>
          <span className="text-xs">Seleccionado</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span className="text-xs">Ocupado</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-gray-300 rounded"></div>
          <span className="text-xs">No disponible</span>
        </div>
      </div>
    </div>
  );
};

// ----- Reducer -----
const bookingReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.loading
      };

    case 'SET_API_DATA': {
      const apiTrip = action.data?.tripData || state.tripInfo;
      const totalSeats = apiTrip?.totalSeats || state.tripInfo.totalSeats || 48;
      
      // For playa/avion trips without bus, use booked seats to determine capacity
      const bookedSeatsData = action.data?.bookedSeats || [];
      const normalizedBooked = bookedSeatsData.map(id => parseSeatId(id));
      
      // If totalSeats is 0 or negative, infer from booked seats or default to 1
      const effectiveTotalSeats = totalSeats > 0 ? totalSeats : Math.max(normalizedBooked.length, 1);
      const normalizedSeats = normalizeBusSeats(action.data?.seats, effectiveTotalSeats, SEATS_PER_ROW);

      // Handle existing booking data - check both nested and root level
      const existingBooking = action.data?.existingBooking || action.data?.booking || action.data;
      
      // Extract passenger details from seats
      const preSelectedSeats = existingBooking?.passengerDetails && Array.isArray(existingBooking.passengerDetails)
        ? existingBooking.passengerDetails.map(p => parseSeatId(p.seat)).filter(s => s)
        : existingBooking?.selectedSeats 
          ? existingBooking.selectedSeats.map(id => parseSeatId(id))
          : [];

      let updatedPassengerDetails = state.passengerDetails;
      let passengerCount = state.passengerCount;
      
      // Prefill passenger count and details from existing booking
      if (existingBooking?.passengerDetails && Array.isArray(existingBooking.passengerDetails)) {
        updatedPassengerDetails = existingBooking.passengerDetails.map(p => ({
          name: p.name || '',
          birthday: p.birthday || '',
          phone: p.phone || '',
          ageCategory: p.ageCategory || 'adulto'
        }));
        passengerCount = updatedPassengerDetails.length;
      } else if (existingBooking?.passengerCount) {
        // Fallback: if passengerCount is provided separately
        passengerCount = existingBooking.passengerCount;
        updatedPassengerDetails = Array(passengerCount).fill().map((_, idx) => 
          state.passengerDetails[idx] || { name: '', birthday: '', phone: '' }
        );
      } else if (action.data?.contact_name) {
        updatedPassengerDetails = state.passengerDetails.map((p, idx) =>
          idx === 0 ? { ...p, name: action.data.contact_name } : p
        );
      }

      const preSelectedRooms = normalizeSelectedRooms(existingBooking?.selectedRooms || {});
      const preRoomAssignments = normalizeRoomAssignments(existingBooking?.roomAssignments || {});

      const prePassengerSeats = existingBooking?.passengerDetails && Array.isArray(existingBooking.passengerDetails)
        ? existingBooking.passengerDetails.map(p => parseSeatId(p.seat)).filter(s => s)
        : [];

      let initialStep = 'asientos';
      if (preSelectedSeats.length > 0 && preSelectedSeats.length === passengerCount) {
        if (Object.keys(preSelectedRooms).length > 0) {
          if (updatedPassengerDetails.every(p => p.name && p.birthday)) {
            initialStep = 'pasajeros';
          } else {
            initialStep = 'pasajeros';
          }
        } else {
          initialStep = 'hoteles';
        }
      }

      return {
        ...state,
        apiData: action.data,
        isLoading: false,
        contact_name: action.data?.contact_name || '',
        hotelData: action.data?.hotelData || [],
        hotelPrecios: action.data?.Hoteles || {},
        tripInfo: apiTrip,
        bookedSeats: normalizedBooked,
        busSeats: normalizedSeats,
        productCategory: action.data?.productCategory || '',
        passengerCount,
        selectedSeats: preSelectedSeats,
        selectedRooms: preSelectedRooms,
        roomAssignments: preRoomAssignments,
        passengerDetails: updatedPassengerDetails,
        passengerSeats: prePassengerSeats.length === passengerCount ? prePassengerSeats : [],
        currentStep: initialStep,
        total: existingBooking?.total || state.total
      };
    }

    case 'SET_API_ERROR':
      return {
        ...state,
        apiError: action.error,
        isLoading: false
      };

    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.step
      };

    case 'SELECT_SEAT':
      if (
        state.selectedSeats.length < state.passengerCount &&
        !state.selectedSeats.includes(action.seatId) &&
        !state.bookedSeats.includes(action.seatId)
      ) {
        return {
          ...state,
          selectedSeats: [...state.selectedSeats, action.seatId]
        };
      }
      return state;

    case 'DESELECT_SEAT': {
      const newSelected = state.selectedSeats.filter(id => id !== action.seatId);
      const newPassengerSeats = (state.passengerSeats || []).map(s => (s === action.seatId ? undefined : s)).filter(Boolean);
      return {
        ...state,
        selectedSeats: newSelected,
        passengerSeats: newPassengerSeats
      };
    }

    case 'ADD_ROOM_TOTAL':
      return {
        ...state,
        total: state.total + action.amount
      };

    case 'SELECT_ROOM': {
      const currentRooms = state.selectedRooms[action.hotelId]?.[action.roomType] || [];
      const maxAvailable = state.hotelData.find(h => Object.keys(h)[0] === action.hotelId)?.[action.hotelId]?.[action.roomType] || 0;

      if (currentRooms.length < maxAvailable) {
        const newRoom = {
          adults: action.adults || 0,
          juniors: action.juniors || 0,
          children: action.children || 0
        };

        const newSelectedRooms = {
          ...state.selectedRooms,
          [action.hotelId]: {
            ...state.selectedRooms[action.hotelId],
            [action.roomType]: [...currentRooms, newRoom]
          }
        };

        const hotelCapacity = calculateHotelCapacity(newSelectedRooms[action.hotelId] || {});
        if (hotelCapacity <= state.passengerCount) {
          return {
            ...state,
            selectedRooms: newSelectedRooms
          };
        }
      }
      return state;
    }

    case 'DESELECT_ROOM': {
      const currentRooms = state.selectedRooms[action.hotelId]?.[action.roomType] || [];
      if (currentRooms.length > 0) {
        const newRooms = currentRooms.slice(0, -1); // Remove last room
        const newHotelRooms = { ...state.selectedRooms[action.hotelId] };

        if (newRooms.length === 0) {
          delete newHotelRooms[action.roomType];
        } else {
          newHotelRooms[action.roomType] = newRooms;
        }

        return {
          ...state,
          selectedRooms: {
            ...state.selectedRooms,
            [action.hotelId]: Object.keys(newHotelRooms).length > 0 ? newHotelRooms : {}
          }
        };
      }
      return state;
    }

    case 'SET_PASSENGER_COUNT': {
      const pool = state.selectedSeats;
      const count = action.count;

      const keep = (state.passengerSeats || []).filter(s => pool.includes(s)).slice(0, count);
      const used = new Set(keep);
      const filled = [...keep];
      for (const s of pool) {
        if (filled.length >= count) break;
        if (!used.has(s)) {
          filled.push(s);
          used.add(s);
        }
      }

      const newPassengerDetails = Array(count).fill().map((_, index) =>
        state.passengerDetails[index] || { name: '', birthday: '', phone: '', ageCategory: 'adulto' }
      );

      return {
        ...state,
        passengerCount: count,
        selectedSeats: state.selectedSeats.slice(0, count),
        selectedRooms: {},
        passengerDetails: newPassengerDetails,
        passengerSeats: filled
      };
    }

    case 'SET_URL_PARAMETERS':
      return {
        ...state,
        urlParameters: action.parameters
      };

    case 'SET_PASSENGER_DETAILS':
      return {
        ...state,
        passengerDetails: action.details
      };

    case 'SET_PASSENGER_SEATS':
      return {
        ...state,
        passengerSeats: action.seats
      };

    case 'SET_PASSENGER_SEAT': {
      const seatId = action.seatId;
      if (!state.selectedSeats.includes(seatId)) return state;

      const curr = [...(state.passengerSeats || [])];
      const i = action.index;
      const j = curr.findIndex((s, idx) => idx !== i && s === seatId);

      if (j >= 0) {
        const tmp = curr[i];
        curr[i] = curr[j];
        curr[j] = tmp;
      } else {
        curr[i] = seatId;
      }
      return { ...state, passengerSeats: curr };
    }

    case 'SET_ROOM_ASSIGNMENTS':
      return {
        ...state,
        roomAssignments: action.roomAssignments
      };

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedSeats: [],
        selectedRooms: {},
        passengerSeats: [],
        passengerDetails: Array(state.passengerCount).fill({ name: '', birthday: '', phone: '', ageCategory: 'adulto' }),
        roomAssignments: {}
      };

    default:
      return state;
  }
};

const BookingProvider = ({ children }) => {
  const initialState = {
    currentStep: 'asientos',
    passengerCount: 1,
    selectedSeats: [],
    selectedRooms: {},
    roomAssignments: {},
    passengerSeats: [],
    bookedSeats,
    busSeats: createBusLayout(tripData.totalSeats || 48, SEATS_PER_ROW),
    hotelData: mockHotelData,
    hotelPrecios:{},
    total: 0,
    passengerDetails: [{ name: '', birthday: '', phone: '', ageCategory: 'adulto' }],
    tripInfo: tripData,
    isLoading: false,
    apiData: null,
    apiError: null,
    urlParameters: null
  };

  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const getUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      id_trip: urlParams.get('id_trip'),
      id_deal : urlParams.get('id_deal'),
    };
  };

  const callAPI = async (id_trip,id_deal) => {
    dispatch({ type: 'SET_LOADING', loading: true });

    const apiUrl = `${baseURL}/${id_trip}?id_deal=${id_deal}`;

    try {
      const response = await fetch(apiUrl);
      console.log('API Request URL:', apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      dispatch({ type: 'SET_API_DATA', data });
    } catch (error) {
      console.error('Error calling API:', error);
      dispatch({ type: 'SET_API_ERROR', error: error.message });
    }
  };

  useEffect(() => {
    const urlParams = getUrlParameters();
    dispatch({ type: 'SET_URL_PARAMETERS', parameters: urlParams });

    if (urlParams.id_trip) {
      callAPI(urlParams.id_trip, urlParams.id_deal);
    } else {
      console.log('No id_trip parameter found in URL');
    }
  }, []);

  return (
    <BookingContext.Provider value={{ state, dispatch }}>
      {state.isLoading && <LoadingSpinner />}
      {children}
    </BookingContext.Provider>
  );
};

const useBookingContext = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBookingContext must be used within BookingProvider');
  }
  return context;
};

// Main App Component
const BookingApp = () => {
  return (
    <BookingProvider>
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
            Sistema de Reservas
          </h1>

          <StepSelector />
          <ApiStatus />
          <TripHeader />
          <TopControls />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-3">
              <BusLayout />
              <HotelRoomSelector />
              <PassengerDetailsForm />
            </div>

            <div>
              <SeatLegend />
            </div>
          </div>
        </div>
      </div>
    </BookingProvider>
  );
};

export default BookingApp;
